import { Box, Typography, Paper, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Badge, Grid } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { CardMembership, Search } from '@mui/icons-material';

import { ColumnTitle, Pagination, ScrollbarCustom, WrapTable } from '../../../../components';
import { useState } from 'react';
import { useGetList } from '../../../../services/subscriptions/useSubscription';
import { IUserDetail } from '../../../../types/User';
import { ISubscription } from '../../../../types/Subscription';
import { convertToDate, convertToDateTime } from '../../../../utils/datetime';
import { capitalize } from 'lodash';
import Copyable from '../../../../components/Copyable';


export const Subscription = ({ userDetail }: { userDetail: IUserDetail }) => {
    const [page, setPage] = useState(1);
    const { data: subscriptionData, isLoading, isFetching } = useGetList(page, { per_page: 10, user_id: userDetail._id });
    const { data: currentSubscriptions } = useGetList(page, { per_page: 10, user_id: userDetail._id, status: 'active' });
    const currentSubscription = currentSubscriptions?.data?.[0];
    const { data: tableData, total } = subscriptionData || { data: [], total: 0, per_page: 10 };

    return (
        <Box sx={{ color: 'white', p: 4 }}>
            <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                        <CardMembership style={{ fill: '#C891FF', width: 32, height: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>Subscription</Typography>
                        <Typography variant="body2" color="gray">Get a detailed breakdown of user subscriptions.</Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<Search />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Search
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Download CSV
                    </Button>
                    <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        <FilterListIcon />
                    </IconButton>
                </Stack>
            </Stack>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4, mb: 4 }}>
                <Grid container spacing={4} mb={2}
                    direction="row"
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}>
                    <Grid item >
                        <Typography variant="body2" color="gray">Subscription</Typography>
                        <Typography variant="h5" fontWeight={600}>{currentSubscription?.subscription_plan_name || '-'}</Typography>
                    </Grid>
                    <Grid item >
                        <Typography variant="body2" color="gray">Period</Typography>
                        <Typography variant="h5" fontWeight={600}>{currentSubscription?.subscription_time || '-'}</Typography>
                    </Grid>
                    <Grid item >
                        <Typography variant="body2" color="gray">Expiry date</Typography>
                        <Typography variant="h5" fontWeight={600}>{currentSubscription?.subscribe_to ? convertToDate(currentSubscription?.subscribe_to) : '-'}</Typography>
                    </Grid>
                    <Grid item >
                        <Typography variant="body2" color="gray">Auto recurring</Typography>
                        <Typography variant="h5" fontWeight={600}>{currentSubscription ? userDetail.subscription_auto_renew ? 'Yes' : 'No' : '-'}</Typography>
                    </Grid>
                    <Grid item >
                        <Typography variant="body2" color="gray">Status</Typography>
                        <Typography variant="h5" fontWeight={600}>{currentSubscription?.status?.toUpperCase() || '-'}</Typography>
                    </Grid>
                </Grid>
            </Paper>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                <Box mb={2}>
                    <WrapTable p={"5px 21px"} height="100%" marginBottom={3} isLoading={isLoading || isFetching}>
                        <TableContainer>
                            <ScrollbarCustom autoHide>
                                <Table sx={{ minWidth: 800, marginBottom: "10px" }}>
                                    <TableHead>
                                        <TableRow>
                                            <ColumnTitle sx={{ pl: 0 }}>SN</ColumnTitle>
                                            <ColumnTitle>Subscription ID</ColumnTitle>
                                            <ColumnTitle>Subscription plan</ColumnTitle>
                                            <ColumnTitle>Period</ColumnTitle>
                                            <ColumnTitle>Charging rate</ColumnTitle>
                                            <ColumnTitle>Total energy</ColumnTitle>
                                            <ColumnTitle>Auto recurring</ColumnTitle>
                                            <ColumnTitle sx={{ minWidth: '180px' }}>Expiry date</ColumnTitle>
                                            <ColumnTitle>Payment method</ColumnTitle>
                                            <ColumnTitle>Payment Status</ColumnTitle>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableData.map((row: ISubscription, index: number) => (
                                            <TableRow key={row._id}>
                                                <TableCell sx={{ pl: 0 }}>{(page - 1) * 10 + index + 1}</TableCell>
                                                <TableCell><Copyable text={row._id} /></TableCell>
                                                <TableCell>{row.subscription_plan_name}</TableCell>
                                                <TableCell>{row.subscription_time}</TableCell>
                                                <TableCell>{row.quotation.charging_rate}</TableCell>
                                                <TableCell>{row.quotation.used_energy || 0}</TableCell>
                                                <TableCell align='center'>
                                                    <Badge badgeContent={row.recurring_payment} color={row.recurring_payment === 'Active' ? 'success' : 'warning'} sx={{ '.MuiBadge-badge': { width: '5rem' } }} />
                                                </TableCell>
                                                <TableCell>{convertToDateTime(row.subscribe_to)}</TableCell>
                                                <TableCell>{row.transaction_info?.payment_type ? row.transaction_info?.payment_type : '-'}</TableCell>
                                                <TableCell align='center'>
                                                    <Badge badgeContent={capitalize(row.payment_status || "")} color={row.payment_status === 'paid' ? 'success' : 'warning'} sx={{ '.MuiBadge-badge': { width: '5rem' } }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ScrollbarCustom>
                        </TableContainer>
                    </WrapTable>
                    <Pagination
                        page={page}
                        setPage={setPage}
                        totalItems={total}
                        totalPage={Math.ceil(total / 10)}
                        currentPage={page}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
